import { Accordion } from "components/Accordion/Accordion";
import { RelationshipRecordForm } from "components/Form/RelationshipRecordForm";
import { FeRecordType } from "config/config";
import { DragAndDropCard } from "containers/DragAndDrop/DragAndDropCard";
import { Resource_Wrapper } from "graphql/generated/graphql";
import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getRelativeUrl } from "utils/service";

interface ResourceWrapperData extends Resource_Wrapper {
    resource_type: string;
    name: string;
}

interface ResourceWrapperProps {
    wrapper: ResourceWrapperData;
    onMoveCard: (dragIndex: any, hoverIndex: any) => void;
    index: any;
    updateOrderAndFetch: (deletedId?: string) => void;
    showModal: (id: string, name: string) => void;
    isDisabled: boolean;
    canDrag: boolean;
    setNestedFormSaveData?: (value: any) => void;
    nestedFormSaveData?: { [key: string]: () => void };
    isNew?: boolean;
    onDelete?: () => void;
    saveNewResourceWrapper?: () => void;
    isExpandedInitially?: boolean;
    parentResourceType?: FeRecordType;
    parentResourceId?: string;
    canTrackInGitParent?: boolean | null;
    isGitEnabled?: boolean;
}

export const ResourceWrapper = ({
    onMoveCard,
    index,
    wrapper,
    updateOrderAndFetch,
    showModal,
    isDisabled,
    canDrag,
    setNestedFormSaveData,
    nestedFormSaveData,
    isNew,
    onDelete,
    saveNewResourceWrapper,
    isExpandedInitially = false,
    parentResourceType,
    parentResourceId,
}: ResourceWrapperProps) => {
    const [isExpanded, setExpanded] = useState(isExpandedInitially);
    const onExpandChange = (expanded) => setExpanded(expanded);

    const getRef = useCallback(
        (node) => node?.scrollIntoView({ behavior: "smooth", block: "center" }),
        []
    );

    const label = wrapper.wrapper_id_wrapper?.name ?? "";
    const wrapperId = wrapper.wrapper_id_wrapper?.id;

    const { wrapper_id_wrapper, ...defaultValues } = wrapper;

    const accordionProps = useMemo(
        () =>
            isNew
                ? {
                      onDelete,
                      isExpanded: true,
                      onExpandedChange: () => {},
                      isExpandedByDefault: true,
                  }
                : {
                      className: isExpanded ? "" : "accordion-hideChildren",
                      onDelete: () => showModal(wrapper.id, label),
                      isExpanded: isExpanded,
                      onExpandedChange: onExpandChange,
                  },
        [isExpanded, isNew, label, onDelete, showModal, wrapper.id]
    );

    const children = (
        <Accordion
            title={
                wrapperId ? (
                    <Link
                        to={getRelativeUrl("wrapper", wrapperId)}
                        className="resourceWrapperLabel"
                    >
                        {label}
                    </Link>
                ) : (
                    label
                )
            }
            isDisabled={isDisabled}
            hasBottomExpandIcon
            {...accordionProps}
        >
            <div ref={isNew ? getRef : null}>
                <RelationshipRecordForm
                    data={{
                        resourceName: "resource_wrapper",
                        recordId: wrapper.id,
                        isDisabled: isDisabled,
                        isReadOnly: isDisabled,
                        parentResourceType,
                        parentResourceId,
                    }}
                    defaultValues={isNew ? defaultValues : null}
                    isNested={true}
                    isOpen={isExpanded}
                    setNestedFormSaveData={setNestedFormSaveData}
                    nestedFormSaveData={nestedFormSaveData}
                    onConfirm={saveNewResourceWrapper}
                />
            </div>
        </Accordion>
    );

    return isNew ? (
        children
    ) : (
        <DragAndDropCard
            onMoveCard={onMoveCard}
            index={index}
            id={wrapper.id}
            applyAction={updateOrderAndFetch}
            type="wrapper"
            canDrag={canDrag}
        >
            {children}
        </DragAndDropCard>
    );
};
